/**
 * Internal building blocks shared by the engine functions (not re-exported by `engine/index.ts`).
 *
 * A `Ledger` holds, for one snapshot, the effects of every movement (workbook columns R..V, Z) and
 * the increment of every report (Report_Punti!K). Every public function builds it once and then
 * applies the sums of the workbook formulas over it. Cancelled movements and reports have effect 0.
 */
import { MOVEMENT_TYPES } from '../constants.js';
import { toRome } from '../time.js';
import type {
  DomainSnapshot,
  HHMM,
  IsoDate,
  IsoDateTime,
  Movement,
  MovementType,
  Point,
  PointReport,
} from '../types.js';

export interface MovementEntry {
  movement: Movement;
  type: MovementType | undefined;
  /** `occurredAt` in ms; NaN when missing or invalid (excluded from every "≤ t" sum, like a blank Excel cell). */
  ms: number;
  /**
   * Operating day used by every per-day sum; null without a valid instant (workbook column N is
   * blank when the date is missing, even with a forced day).
   */
  day: IsoDate | null;
  /** Quantity with effect: 0 when cancelled. */
  quantity: number;
  deltaHub: number;
  /** Effect on `movement.pointId` (0 without a point). */
  deltaPoint: number;
  deltaDefective: number;
}

export interface ReportEntry {
  report: PointReport;
  /** `reportedAt` in ms; NaN when missing or invalid. */
  ms: number;
  /** Rome date of `reportedAt` ("Data" of the workbook); '' when invalid. */
  date: IsoDate;
  time: HHMM;
  /** Distributed since the previous report of the same point and date; 0 when cancelled. */
  increment: number;
}

export interface Ledger {
  snap: DomainSnapshot;
  movements: MovementEntry[];
  reports: ReportEntry[];
  /** Points ordered by `sortOrder`, then `id`. */
  points: Point[];
}

const FALLBACK_TYPES = new Map<number, MovementType>(MOVEMENT_TYPES.map((t) => [t.id, t]));

/** Instant in ms; NaN for missing or invalid values. */
export function instantMs(iso: IsoDateTime | null | undefined): number {
  return iso ? Date.parse(iso) : Number.NaN;
}

function movementEntry(movement: Movement, types: Map<number, MovementType>): MovementEntry {
  const type = types.get(movement.typeId) ?? FALLBACK_TYPES.get(movement.typeId);
  const quantity =
    movement.cancelled || !Number.isFinite(movement.quantity) ? 0 : movement.quantity;
  const hubSign = type?.hubSign ?? 0;
  const pointSign = movement.pointId == null ? 0 : (type?.pointSign ?? 0);
  const defectiveSign = type?.defectiveSign ?? 0;
  const ms = instantMs(movement.occurredAt);
  return {
    movement,
    type,
    ms,
    day: Number.isNaN(ms) || !movement.operatingDay ? null : movement.operatingDay,
    quantity,
    // `+ 0` turns -0 into 0
    deltaHub: hubSign * quantity + 0,
    deltaPoint: pointSign * quantity + 0,
    deltaDefective: defectiveSign * quantity + 0,
  };
}

/**
 * Report_Punti!K: progressive − MAX(progressives of the same point and date with an earlier
 * instant), 0 when there is none. Cancelled reports have increment 0 and are ignored by the others.
 */
function reportEntries(reports: PointReport[]): ReportEntry[] {
  const base = reports.map((report) => {
    const ms = instantMs(report.reportedAt);
    const rome = Number.isNaN(ms) ? null : toRome(report.reportedAt);
    return { report, ms, date: rome?.date ?? '', time: rome?.time ?? '' };
  });
  // valid reports grouped by point and date: MAXIFS only looks inside the group
  const groups = new Map<string, typeof base>();
  for (const entry of base) {
    if (entry.report.cancelled || Number.isNaN(entry.ms)) continue;
    const key = `${entry.report.pointId}|${entry.date}`;
    const group = groups.get(key) ?? [];
    group.push(entry);
    groups.set(key, group);
  }
  return base.map((entry) => {
    const { report, ms, date } = entry;
    if (report.cancelled || Number.isNaN(ms) || !Number.isFinite(report.progressiveDistributed)) {
      return { ...entry, increment: 0 };
    }
    let previousMax = 0;
    for (const other of groups.get(`${report.pointId}|${date}`) ?? []) {
      if (other.ms < ms && other.report.progressiveDistributed > previousMax) {
        previousMax = other.report.progressiveDistributed;
      }
    }
    return { ...entry, increment: report.progressiveDistributed - previousMax };
  });
}

export function buildLedger(snap: DomainSnapshot): Ledger {
  const types = new Map<number, MovementType>(snap.movementTypes.map((t) => [t.id, t]));
  const points = [...snap.points].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
  return {
    snap,
    movements: snap.movements.map((m) => movementEntry(m, types)),
    reports: reportEntries(snap.reports),
    points,
  };
}

// ---------------------------------------------------------------- sums over instants (≤ t)

/** Σ Δ hub of the movements with occurredAt ≤ t. */
export function hubAt(l: Ledger, ms: number): number {
  let sum = 0;
  for (const e of l.movements) if (e.ms <= ms) sum += e.deltaHub;
  return sum;
}

/** Σ Δ defective of the movements with occurredAt ≤ t. */
export function defectiveAt(l: Ledger, ms: number): number {
  let sum = 0;
  for (const e of l.movements) if (e.ms <= ms) sum += e.deltaDefective;
  return sum;
}

/** Σ report increments of a point with reportedAt ≤ t. */
export function distributedAt(l: Ledger, pointId: number, ms: number): number {
  let sum = 0;
  for (const r of l.reports) if (r.report.pointId === pointId && r.ms <= ms) sum += r.increment;
  return sum;
}

/** Point stock at t: Σ Δ point of the movements ≤ t − Σ report increments of the point ≤ t. */
export function pointStockAt(l: Ledger, pointId: number, ms: number): number {
  let sum = 0;
  for (const e of l.movements) {
    if (e.movement.pointId === pointId && e.ms <= ms) sum += e.deltaPoint;
  }
  return sum - distributedAt(l, pointId, ms);
}

/** Σ effective quantity of the movements of a type matching `where`. */
export function sumQuantity(
  l: Ledger,
  typeId: number,
  where: (e: MovementEntry) => boolean = () => true,
): number {
  let sum = 0;
  for (const e of l.movements) if (e.movement.typeId === typeId && where(e)) sum += e.quantity;
  return sum;
}

/** Σ report increments matching `where`. */
export function sumIncrements(l: Ledger, where: (r: ReportEntry) => boolean): number {
  let sum = 0;
  for (const r of l.reports) if (where(r)) sum += r.increment;
  return sum;
}

/** Operating days of the event (active ones), sorted by date. */
export function eventDays(snap: DomainSnapshot): IsoDate[] {
  return snap.operatingDays
    .filter((d) => d.active)
    .map((d) => d.date)
    .sort();
}

/**
 * Excel works with 15 significant digits: `ROUNDUP(11000 × 1.1, 0)` is 12.100 although the binary
 * product is 12100.000000000002. Rounding to 15 significant digits first reproduces it.
 */
export function excelPrecision(value: number): number {
  return Number(value.toPrecision(15));
}
